/**
 * Combines every pattern detector's output into a final `HandScore`, per
 * `scoring/types.ts`'s `PatternKind` doc: base points sum, then the running
 * total is doubled once per stacked double, UNLESS any `'limit'` pattern
 * matched — in which case the total is the fixed traditional limit
 * regardless of what else matched. Every matched pattern (base, double, AND
 * limit) still appears in `patterns` even in the limit case, so the display
 * can show "you also had X, but it didn't matter — you got the limit."
 */

import { matchAllSimples } from './patterns/allSimples.js';
import { matchAllTriplets } from './patterns/allTriplets.js';
import { computeBasePoints } from './patterns/basePoints.js';
import { matchFlush } from './patterns/flush.js';
import { matchGreatDragons, matchGreatWinds } from './patterns/greatHonors.js';
import { matchEarthlyHand, matchHeavenlyHand } from './patterns/heavenlyEarthly.js';
import { matchAllHonors, matchTerminals } from './patterns/honorsAndTerminals.js';
import { matchSevenPairs } from './patterns/sevenPairs.js';
import { matchThirteenOrphans } from './patterns/thirteenOrphans.js';
import type { HandScore, MatchedPattern, ScoringContext } from './types.js';

/** Traditional fixed cap for any limit hand — see `scoring/types.ts`'s `PatternKind` doc. */
const LIMIT_HAND_POINTS = 300;

const DOUBLE_BASE = 2;

/** Single-match detectors, in no particular order — each returns at most one pattern. */
const SINGLE_MATCH_DETECTORS: ReadonlyArray<(context: ScoringContext) => MatchedPattern | null> = [
  matchAllTriplets,
  matchAllSimples,
  matchFlush,
  matchAllHonors,
  matchTerminals,
  matchGreatDragons,
  matchGreatWinds,
  matchThirteenOrphans,
  matchHeavenlyHand,
  matchEarthlyHand,
];

function collectPatterns(context: ScoringContext): MatchedPattern[] {
  const patterns: MatchedPattern[] = [...computeBasePoints(context), ...matchSevenPairs(context)];
  for (const detector of SINGLE_MATCH_DETECTORS) {
    const matched = detector(context);
    if (matched !== null) patterns.push(matched);
  }
  return patterns;
}

function sumByKind(patterns: readonly MatchedPattern[], kind: MatchedPattern['kind']): number {
  return patterns.filter((pattern) => pattern.kind === kind).reduce((total, pattern) => total + pattern.value, 0);
}

/** Runs every scoring-pattern detector and combines the results into a final `HandScore`. */
export function calculateHandScore(context: ScoringContext): HandScore {
  const patterns = collectPatterns(context);

  const hasLimit = patterns.some((pattern) => pattern.kind === 'limit');
  const points = hasLimit
    ? LIMIT_HAND_POINTS
    : sumByKind(patterns, 'base') * Math.pow(DOUBLE_BASE, sumByKind(patterns, 'double'));

  return { points, patterns };
}
